import React, { useState, useEffect, useRef } from "react";
import { Sparkles, Copy, Wand2, ArrowLeft, Loader2, Download, Music, Search, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const GENRE_OPTIONS = [
  "trap", "drill", "boom bap", "phonk", "hip hop",
  "house", "techno", "synthwave", "edm", "dubstep",
  "lo-fi", "ambient", "chillhop",
  "jazz", "blues", "soul", "r&b", "gospel", "funk",
  "orchestral", "cinematic", "epic",
  "rock", "metal", "punk", "grunge",
  "pop", "k-pop", "synth pop",
  "country", "folk", "americana",
  "afrobeats", "amapiano", "reggaeton", "dancehall", "latin",
];

export const AutonomousPromptLab = ({ onBack, apiBase, onUsePrompt, saveWavFromUrl, onGenerateMidi, onSaveToLibrary, genreOptions, email }) => {
  const genres = ["G-Funk (Cali)", ...((genreOptions && genreOptions.length) ? genreOptions : GENRE_OPTIONS)].filter((g, i, a) => a.indexOf(g) === i);
  const mediaOrigin = (apiBase || "").replace(/\/api\/?$/, "");
  const mediaSrc = (u) => (u && u.startsWith("/") ? `${mediaOrigin}${u}` : u);
  const [selected, setSelected] = useState(() => new Set());
  const [count, setCount] = useState(4);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [gen, setGen] = useState({}); // per-row generation state: { [idx]: {status, progress, audioUrl} }
  const [genreSearch, setGenreSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [bpm, setBpm] = useState("");
  const [musicKey, setMusicKey] = useState("");
  const KEY_OPTIONS = ["C major","C minor","C# minor","D major","D minor","Eb major","E minor","E major","F major","F minor","F# minor","G major","G minor","A minor","A major","Bb major","B minor"];
  const dropMatches = genres.filter((g) => g.toLowerCase().includes(genreSearch.trim().toLowerCase()));

  // Persistent background jobs (survive leaving the app)
  const [jobs, setJobs] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const jobsRef = useRef([]);
  jobsRef.current = jobs;

  const loadJobs = async () => {
    if (!email) return;
    try {
      const r = await fetch(`${apiBase}/prompt-lab/jobs?email=${encodeURIComponent(email)}`);
      if (!r.ok) return;
      const d = await r.json();
      setJobs(d.jobs || []);
    } catch { /* transient */ }
  };

  // Load on mount so returning users immediately see ready audio
  useEffect(() => { loadJobs(); /* eslint-disable-next-line */ }, []);

  // Keep refreshing while anything is still rendering
  useEffect(() => {
    const anyPending = jobs.some((j) => j.status === "generating");
    if (!anyPending) return;
    const t = setInterval(loadJobs, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [jobs]);

  const generateAll = async () => {
    if (!results.length) { toast.error("Compile some prompts first"); return; }
    setBatchLoading(true);
    try {
      const items = results.map((r) => ({ genre: r.genre, variation_index: r.variation_index, prompt: r.prompt, bpm: r.bpm, key: r.key }));
      const resp = await fetch(`${apiBase}/prompt-lab/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, duration: 30, items }),
      });
      if (resp.status === 402) {
        const e = await resp.json().catch(() => ({}));
        toast.error(typeof e.detail === "string" ? e.detail : "A paid plan or tokens are required to generate audio");
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const d = await resp.json();
      toast.success(`🎧 Generating ${d.count} track(s) in the background — you can leave the app; they'll be ready here when you return`);
      loadJobs();
    } catch (e) {
      console.error(e);
      toast.error("Could not start batch generation");
    } finally {
      setBatchLoading(false);
    }
  };

  const clearJobs = async () => {
    if (!email) return;
    try {
      await fetch(`${apiBase}/prompt-lab/clear`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setJobs([]);
      toast.success("Cleared your saved generations");
    } catch { toast.error("Could not clear"); }
  };

  const pollBeat = async (taskId, idx) => {
    for (let i = 0; i < 200; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const rs = await fetch(`${apiBase}/beat-status/${taskId}`);
        if (!rs.ok) continue;
        const s = await rs.json();
        setGen((g) => ({ ...g, [idx]: { status: s.status === "completed" ? "done" : (s.status === "failed" ? "failed" : "generating"), progress: s.progress || g[idx]?.progress || 0, audioUrl: s.audio_url || g[idx]?.audioUrl || null } }));
        if (s.status === "completed" && s.audio_url) { toast.success("🎧 Audio ready!"); return; }
        if (s.status === "failed") { toast.error(s.error || "Generation failed"); return; }
      } catch { /* transient — keep polling */ }
    }
  };

  const generateAudio = async (prompt, idx) => {
    setGen((g) => ({ ...g, [idx]: { status: "generating", progress: 5, audioUrl: null } }));
    try {
      const r = await fetch(`${apiBase}/generate-beat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, duration: 30, email: email || "guest@sounddna.app", enhance: false, source: "prompt-lab" }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      toast.success("🎧 Generating audio — this usually takes a few minutes");
      pollBeat(d.task_id, idx);
    } catch (e) {
      console.error(e);
      setGen((g) => ({ ...g, [idx]: { status: "failed", progress: 0, audioUrl: null } }));
      toast.error("Could not start audio generation");
    }
  };

  const toggleGenre = (g) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(g) ? next.delete(g) : next.add(g);
      return next;
    });

  const compile = async () => {
    const genres = [...selected];
    if (genres.length === 0) { toast.error("Turn on at least one genre"); return; }
    setLoading(true);
    setResults([]);
    try {
      const resp = await fetch(`${apiBase}/compile-prompt-variations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ genres, count: Number(count) || 1, bpm: bpm ? Number(bpm) : null, key: musicKey || null }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setResults(data.variations || []);
      toast.success(`Compiled ${data.total} premium prompts`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to compile prompts — please try again");
    } finally {
      setLoading(false);
    }
  };

  const copyPrompt = (text) => {
    try { navigator.clipboard.writeText(text); } catch { /* noop */ }
    toast.success("Prompt copied");
  };

  return (
    <div data-testid="autonomous-prompt-lab" style={{ maxWidth: "1000px", margin: "0 auto", width: "100%", padding: "clamp(12px,3vw,24px)" }}>
      <button
        onClick={onBack}
        data-testid="prompt-lab-back-btn"
        style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "10px", color: "#fff", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", marginBottom: "16px" }}
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div style={{ marginBottom: "18px" }}>
        <h1 style={{ fontSize: "clamp(1.6rem,4vw,2.2rem)", fontWeight: 900, color: "#fff", margin: 0, letterSpacing: "-0.02em", display: "flex", alignItems: "center", gap: "10px" }}>
          <Sparkles className="w-6 h-6" style={{ color: "#a855f7" }} /> Autonomous Prompt Lab
        </h1>
        <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", margin: "6px 0 0" }}>
          Toggle genres on, choose how many variations, and auto-compile premium, studio-mastered Suno v5.5 prompts — each one unique.
        </p>
        <div style={{ marginTop: "12px", padding: "10px 14px", background: "rgba(0,212,255,0.08)", border: "1px solid rgba(0,212,255,0.25)", borderRadius: "10px", color: "rgba(255,255,255,0.8)", fontSize: "0.76rem", lineHeight: 1.5 }}>
          🎧 These are ready-to-use <strong>Style Prompts</strong>. Tap <strong style={{ color: "#4ade80" }}>Use in Workflow</strong> on any prompt to load it into the Unified Workflow, where you can generate <strong>Audio</strong> and download <strong>WAV</strong>, <strong>MIDI</strong> and <strong>Studio WAV</strong>.
        </div>
      </div>

      {/* Genre search */}
      <div style={{ position: "relative", marginBottom: "10px" }}>
        <Search className="w-4 h-4" style={{ position: "absolute", left: "12px", top: "20px", transform: "translateY(-50%)", color: "rgba(255,255,255,0.4)", pointerEvents: "none" }} />
        <input
          data-testid="prompt-lab-genre-search"
          value={genreSearch}
          onChange={(e) => { setGenreSearch(e.target.value); setSearchOpen(true); }}
          onFocus={() => setSearchOpen(true)}
          onBlur={() => setTimeout(() => setSearchOpen(false), 180)}
          placeholder="Search 168 genres…"
          style={{ width: "100%", padding: "10px 12px 10px 36px", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(168,85,247,0.3)", borderRadius: "10px", color: "#fff", fontSize: "0.82rem", outline: "none" }}
        />
        {searchOpen && dropMatches.length > 0 && (
          <div
            data-testid="prompt-lab-genre-dropdown"
            style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 40, maxHeight: "260px", overflowY: "auto", background: "#14101f", border: "1px solid rgba(168,85,247,0.4)", borderRadius: "12px", boxShadow: "0 12px 40px rgba(0,0,0,0.6)", padding: "6px" }}
          >
            {dropMatches.slice(0, 80).map((g) => {
              const on = selected.has(g);
              return (
                <button
                  key={g}
                  type="button"
                  data-testid={`prompt-lab-drop-${g.replace(/\s+/g, "-")}`}
                  onMouseDown={(e) => { e.preventDefault(); toggleGenre(g); }}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "9px 12px", background: on ? "rgba(168,85,247,0.18)" : "transparent", border: "none", borderRadius: "8px", color: on ? "#e9d5ff" : "rgba(255,255,255,0.82)", fontSize: "0.8rem", fontWeight: 600, textTransform: "capitalize", cursor: "pointer", textAlign: "left" }}
                >
                  {g}
                  <span style={{ fontSize: "0.72rem", color: on ? "#4ade80" : "rgba(255,255,255,0.35)", fontWeight: 800 }}>{on ? "✓ on" : "+ add"}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Genre toggles */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "18px", maxHeight: "220px", overflowY: "auto", padding: "4px", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px" }}>
        {genres.filter((g) => g.toLowerCase().includes(genreSearch.trim().toLowerCase())).map((g) => {
          const on = selected.has(g);
          return (
            <button
              key={g}
              data-testid={`prompt-lab-genre-${g.replace(/\s+/g, "-")}`}
              aria-pressed={on}
              onClick={() => toggleGenre(g)}
              style={{ padding: "8px 14px", borderRadius: "999px", cursor: "pointer", fontSize: "0.78rem", fontWeight: 700, textTransform: "capitalize", background: on ? "linear-gradient(135deg,#a855f7,#6366f1)" : "rgba(255,255,255,0.05)", border: `1px solid ${on ? "#a855f7" : "rgba(255,255,255,0.14)"}`, color: on ? "#fff" : "rgba(255,255,255,0.7)", transition: "background-color 0.2s, border-color 0.2s" }}
            >
              {g}
            </button>
          );
        })}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", marginBottom: "20px" }}>
        <label style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
          Variations per genre
          <input
            data-testid="prompt-lab-count"
            type="number"
            min={1}
            max={20}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            style={{ width: "70px", padding: "8px 10px", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(168,85,247,0.4)", borderRadius: "8px", color: "#fff", fontSize: "0.85rem", outline: "none" }}
          />
        </label>
        <label style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
          BPM
          <input
            data-testid="prompt-lab-bpm"
            type="number"
            min={40}
            max={220}
            value={bpm}
            onChange={(e) => setBpm(e.target.value)}
            placeholder="Auto"
            style={{ width: "84px", padding: "8px 10px", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(168,85,247,0.4)", borderRadius: "8px", color: "#fff", fontSize: "0.85rem", outline: "none" }}
          />
        </label>
        <label style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
          Key
          <select
            data-testid="prompt-lab-key"
            value={musicKey}
            onChange={(e) => setMusicKey(e.target.value)}
            style={{ padding: "8px 10px", background: "rgba(0,0,0,0.4)", border: "1px solid rgba(168,85,247,0.4)", borderRadius: "8px", color: "#fff", fontSize: "0.85rem", outline: "none" }}
          >
            <option value="">Auto</option>
            {KEY_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <button
          data-testid="prompt-lab-compile-btn"
          onClick={compile}
          disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 24px", background: loading ? "rgba(168,85,247,0.4)" : "linear-gradient(135deg,#a855f7,#6366f1)", border: "none", borderRadius: "10px", color: "#fff", fontSize: "0.85rem", fontWeight: 800, cursor: loading ? "wait" : "pointer", boxShadow: "0 4px 16px rgba(168,85,247,0.4)" }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {loading ? "Compiling…" : "Auto-Compile Prompts"}
        </button>
        <button
          data-testid="prompt-lab-erase-btn"
          onClick={() => { setResults([]); setSelected(new Set()); setGen({}); toast.success("Cleared"); }}
          disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "12px 18px", background: "rgba(239,68,68,0.14)", border: "1px solid rgba(239,68,68,0.5)", borderRadius: "10px", color: "#fca5a5", fontSize: "0.82rem", fontWeight: 800, cursor: loading ? "not-allowed" : "pointer" }}
        >
          <Trash2 className="w-4 h-4" /> Erase
        </button>
        {results.length > 0 && (
          <button
            data-testid="prompt-lab-generate-all-btn"
            onClick={generateAll}
            disabled={batchLoading}
            style={{ display: "flex", alignItems: "center", gap: "8px", padding: "12px 22px", background: batchLoading ? "rgba(34,197,94,0.4)" : "linear-gradient(135deg,#22c55e,#059669)", border: "none", borderRadius: "10px", color: "#fff", fontSize: "0.85rem", fontWeight: 800, cursor: batchLoading ? "wait" : "pointer", boxShadow: "0 4px 16px rgba(34,197,94,0.35)" }}
          >
            {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Music className="w-4 h-4" />}
            {batchLoading ? "Starting…" : `Generate All Audio (${results.length})`}
          </button>
        )}
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem" }}>{selected.size} genre(s) on · {results.length} compiled</span>
      </div>

      {/* Persistent background generations — ready to play when you return */}
      {jobs.length > 0 && (
        <div data-testid="prompt-lab-jobs-panel" style={{ marginBottom: "22px", padding: "14px 16px", background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.28)", borderRadius: "14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px", flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#fff", display: "flex", alignItems: "center", gap: "8px" }}>
              <Music className="w-4 h-4" style={{ color: "#4ade80" }} /> Your Audio · ready to play
            </h2>
            <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)" }}>{jobs.filter((j) => j.status === "done").length}/{jobs.length} ready · saved to your account</span>
            <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
              <button data-testid="prompt-lab-jobs-refresh" onClick={async () => { setJobsLoading(true); await loadJobs(); setJobsLoading(false); }} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "8px", color: "#fff", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer" }}>
                <RefreshCw className={`w-3.5 h-3.5 ${jobsLoading ? "animate-spin" : ""}`} /> Refresh
              </button>
              <button data-testid="prompt-lab-jobs-clear" onClick={clearJobs} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", background: "rgba(239,68,68,0.14)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: "8px", color: "#fca5a5", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer" }}>
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </button>
            </div>
          </div>
          {jobs.some((j) => j.status === "generating") && (
            <p style={{ margin: "0 0 12px", fontSize: "0.72rem", color: "#fbbf24" }}>⏳ Some tracks are still rendering in the background — you can close the app and come back; they'll be waiting here.</p>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {jobs.map((j) => (
              <div key={j.id} data-testid={`prompt-lab-job-${j.id}`} style={{ padding: "12px 14px", background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "0.62rem", fontWeight: 800, padding: "3px 8px", borderRadius: "6px", background: "rgba(168,85,247,0.18)", color: "#c4b5fd", textTransform: "capitalize" }}>{j.genre || "Custom"}</span>
                  {j.bpm && <span style={{ fontSize: "0.62rem", fontWeight: 800, padding: "3px 8px", borderRadius: "6px", background: "rgba(34,197,94,0.16)", color: "#86efac" }}>{j.bpm} BPM</span>}
                  {j.key && <span style={{ fontSize: "0.62rem", fontWeight: 800, padding: "3px 8px", borderRadius: "6px", background: "rgba(0,212,255,0.14)", color: "#7dd3fc", textTransform: "capitalize" }}>{j.key}</span>}
                  {j.status === "generating" && <span style={{ fontSize: "0.62rem", fontWeight: 800, color: "#fbbf24", display: "flex", alignItems: "center", gap: "5px" }}><Loader2 className="w-3 h-3 animate-spin" /> Rendering… {j.progress || 0}%</span>}
                  {j.status === "failed" && <span style={{ fontSize: "0.62rem", fontWeight: 800, color: "#fca5a5" }}>Failed{j.error ? ` — ${j.error}` : ""}</span>}
                  {j.status === "done" && <span style={{ fontSize: "0.62rem", fontWeight: 800, color: "#4ade80" }}>✓ Ready</span>}
                </div>
                <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.72rem", lineHeight: 1.45, margin: "0 0 8px" }}>{j.prompt}</p>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center", marginBottom: (j.status === "done" && j.audio_url) ? "10px" : 0 }}>
                  <button data-testid={`prompt-lab-job-copy-${j.id}`} onClick={() => copyPrompt(j.prompt)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "8px", color: "#fff", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}>
                    <Copy className="w-3.5 h-3.5" /> Copy Style Prompt
                  </button>
                  {onUsePrompt && (
                    <button data-testid={`prompt-lab-job-use-${j.id}`} onClick={() => onUsePrompt(j.prompt)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(99,102,241,0.18)", border: "1px solid rgba(99,102,241,0.5)", borderRadius: "8px", color: "#c7d2fe", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}>
                      <Wand2 className="w-3.5 h-3.5" /> Use in Workflow →
                    </button>
                  )}
                  {onSaveToLibrary && (
                    <button data-testid={`prompt-lab-job-save-${j.id}`} onClick={() => onSaveToLibrary({ genre: j.genre, variation_index: j.variation_index, prompt: j.prompt, bpm: j.bpm, key: j.key }, j.audio_url || null)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(245,158,11,0.16)", border: "1px solid rgba(245,158,11,0.5)", borderRadius: "8px", color: "#fcd34d", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}>
                      💾 Save to Library
                    </button>
                  )}
                </div>
                {j.status === "done" && j.audio_url && (
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                    <audio data-testid={`prompt-lab-job-audio-${j.id}`} controls src={mediaSrc(j.audio_url)} style={{ height: "34px", maxWidth: "240px", verticalAlign: "middle" }} />
                    <button data-testid={`prompt-lab-job-wav-${j.id}`} onClick={() => saveWavFromUrl && saveWavFromUrl(j.audio_url, `${(j.genre || "prompt").replace(/\s+/g, "_")}_v${j.variation_index}.wav`)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "linear-gradient(135deg,#22c55e,#059669)", border: "none", borderRadius: "8px", color: "#fff", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}>
                      <Download className="w-3.5 h-3.5" /> WAV
                    </button>
                    {onGenerateMidi && (
                      <button data-testid={`prompt-lab-job-midi-${j.id}`} onClick={() => onGenerateMidi(j.prompt)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(168,85,247,0.18)", border: "1px solid rgba(168,85,247,0.5)", borderRadius: "8px", color: "#e9d5ff", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}>
                        <Download className="w-3.5 h-3.5" /> MIDI
                      </button>
                    )}
                  </div>
                )}
                {j.status === "generating" && (
                  <div style={{ height: "5px", background: "rgba(255,255,255,0.1)", borderRadius: "3px", overflow: "hidden" }}>
                    <div style={{ width: `${j.progress || 0}%`, height: "100%", background: "linear-gradient(90deg,#22c55e,#00d4ff)", transition: "width 0.5s" }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {results.length === 0 ? (
        <div data-testid="prompt-lab-empty" style={{ padding: "44px 20px", textAlign: "center", color: "rgba(255,255,255,0.45)", border: "1px dashed rgba(255,255,255,0.15)", borderRadius: "14px", fontSize: "0.9rem" }}>
          Turn on some genres and hit <strong style={{ color: "#c4b5fd" }}>Auto-Compile Prompts</strong> to generate premium blueprints.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {results.map((r, idx) => (
            <div
              key={`${r.genre}-${r.variation_index}-${idx}`}
              data-testid={`prompt-lab-result-${idx}`}
              style={{ padding: "14px 16px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: "12px" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.62rem", fontWeight: 800, padding: "3px 8px", borderRadius: "6px", background: "rgba(168,85,247,0.18)", color: "#c4b5fd", textTransform: "capitalize" }}>{r.genre}</span>
                {r.bpm && <span data-testid={`prompt-lab-bpm-badge-${idx}`} style={{ fontSize: "0.62rem", fontWeight: 800, padding: "3px 8px", borderRadius: "6px", background: "rgba(34,197,94,0.16)", color: "#86efac" }}>{r.bpm} BPM</span>}
                {r.key && <span data-testid={`prompt-lab-key-badge-${idx}`} style={{ fontSize: "0.62rem", fontWeight: 800, padding: "3px 8px", borderRadius: "6px", background: "rgba(0,212,255,0.14)", color: "#7dd3fc", textTransform: "capitalize" }}>{r.key}</span>}
                <span style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.4)" }}>Variation #{r.variation_index}</span>
              </div>
              <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.78rem", lineHeight: 1.5, margin: "0 0 10px" }}>{r.prompt}</p>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                <button
                  data-testid={`prompt-lab-copy-${idx}`}
                  onClick={() => copyPrompt(r.prompt)}
                  style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "8px", color: "#fff", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
                {onUsePrompt && (
                  <button
                    data-testid={`prompt-lab-use-${idx}`}
                    onClick={() => onUsePrompt(r.prompt)}
                    style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(99,102,241,0.18)", border: "1px solid rgba(99,102,241,0.5)", borderRadius: "8px", color: "#c7d2fe", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}
                  >
                    <Wand2 className="w-3.5 h-3.5" /> Use in Workflow →
                  </button>
                )}
                {onSaveToLibrary && (
                  <button
                    data-testid={`prompt-lab-save-${idx}`}
                    onClick={() => onSaveToLibrary(r, gen[idx]?.audioUrl || null)}
                    style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(245,158,11,0.16)", border: "1px solid rgba(245,158,11,0.5)", borderRadius: "8px", color: "#fcd34d", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}
                  >
                    💾 Save to Library
                  </button>
                )}

                {/* In-Lab audio generation */}
                {(!gen[idx] || gen[idx].status === "failed") && (
                  <button
                    data-testid={`prompt-lab-generate-${idx}`}
                    onClick={() => generateAudio(r.prompt, idx)}
                    style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 16px", background: "linear-gradient(135deg,#22c55e,#059669)", border: "none", borderRadius: "8px", color: "#fff", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}
                  >
                    <Music className="w-3.5 h-3.5" /> {gen[idx]?.status === "failed" ? "Retry Audio" : "Generate Audio"}
                  </button>
                )}
                {gen[idx]?.status === "generating" && (
                  <span data-testid={`prompt-lab-progress-${idx}`} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.72rem", color: "#fbbf24", fontWeight: 700 }}>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Rendering… {gen[idx].progress || 0}%
                  </span>
                )}
                {gen[idx]?.status === "done" && gen[idx].audioUrl && (
                  <>
                    <audio
                      data-testid={`prompt-lab-audio-${idx}`}
                      controls
                      src={mediaSrc(gen[idx].audioUrl)}
                      style={{ height: "34px", maxWidth: "220px", verticalAlign: "middle" }}
                    />
                    <button
                      data-testid={`prompt-lab-wav-${idx}`}
                      onClick={() => saveWavFromUrl && saveWavFromUrl(gen[idx].audioUrl, `${r.genre.replace(/\s+/g, "_")}_v${r.variation_index}.wav`)}
                      style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "linear-gradient(135deg,#22c55e,#059669)", border: "none", borderRadius: "8px", color: "#fff", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}
                    >
                      <Download className="w-3.5 h-3.5" /> WAV
                    </button>
                    {onGenerateMidi && (
                      <button
                        data-testid={`prompt-lab-midi-${idx}`}
                        onClick={() => onGenerateMidi(r.prompt)}
                        style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "rgba(168,85,247,0.18)", border: "1px solid rgba(168,85,247,0.5)", borderRadius: "8px", color: "#e9d5ff", fontSize: "0.72rem", fontWeight: 800, cursor: "pointer" }}
                      >
                        <Download className="w-3.5 h-3.5" /> MIDI
                      </button>
                    )}
                  </>
                )}
              </div>

              {gen[idx]?.status === "generating" && (
                <div style={{ height: "5px", background: "rgba(255,255,255,0.1)", borderRadius: "3px", overflow: "hidden", marginTop: "10px" }}>
                  <div style={{ width: `${gen[idx].progress || 0}%`, height: "100%", background: "linear-gradient(90deg,#22c55e,#00d4ff)", transition: "width 0.5s" }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutonomousPromptLab;
